import { bianka, BIANKA_ACTIVITY_TYPES, ReportActivityOptions, AdditionalData } from '@fiverr-private/obs';
import { Click, Impression } from '@fiverr-private/bianka_js';
import { ViewedTierLevelUpdated } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/loyalty/viewed_tier_level_updated';
import { ViewedLoyaltyCreditsGiven } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/loyalty/viewed_loyalty_credits_given';
import { ViewedLoyaltyCreditsOnTheWay } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/loyalty/viewed_loyalty_credits_on_the_way';
import { ViewedLoyaltyCreditIndicationBadge } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/loyalty/viewed_loyalty_credit_indication_badge';
import {
    ComponentType,
    TierLevel,
    EventSource,
} from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/loyalty/common';
import { getContext } from '@fiverr-private/fiverr_context';
import { Gql } from '@fiverr-private/loyalty_utils';
import { BannerType } from '../components/LoyaltyIndicationBanner/bannerPropsTypes';
import { BannerType as BannerTypePoints } from '../components/LoyaltyIndicationBannerPointsMode/bannerPropsTypes';

interface BqEventPayload {
    tierLevel: Gql.LoyaltyProgramTier;
    pageName: string;
    componentName?: string;
    componentType?: ComponentType;
}

type TEvent = ViewedTierLevelUpdated;

interface ExtraData<T extends TEvent> {
    pageName: string;
    event: {
        type: ReportActivityOptions<Click | Impression, T>['event']['type'];
    };
    additionalData: {
        type: AdditionalData<T>['type'];
        message: AdditionalData<T>['message'];
    };
}

// TODO: remove BannerTypePoints from here when we add events for points
interface LoyaltyCreditIndicationEventPayload extends BqEventPayload {
    componentName: BannerType | BannerTypePoints;
    pageName: BannerType | BannerTypePoints;
}

const TIER_LEVEL_MAP: Record<Gql.LoyaltyProgramTier, TierLevel> = {
    [Gql.LoyaltyProgramTier.Silver]: TierLevel.SILVER,
    [Gql.LoyaltyProgramTier.Gold]: TierLevel.GOLD,
    [Gql.LoyaltyProgramTier.Platinum]: TierLevel.PLATINUM,
    [Gql.LoyaltyProgramTier.Diamond]: TierLevel.DIAMOND,
};

const getTierLevel = (tier: Gql.LoyaltyProgramTier): TierLevel => TIER_LEVEL_MAP[tier] || TierLevel.UNSPECIFIED;

export const eventsApi = {
    reportImpressionEvent: <T extends TEvent>(data: ExtraData<T>) => {
        const { pageCtxId } = getContext();
        bianka.reportActivity({
            type: BIANKA_ACTIVITY_TYPES.IMPRESSION,
            event: data.event,
            page: {
                name: data.pageName,
                ctxId: pageCtxId,
            },
            additionalData: data.additionalData,
        });
    },

    reportClickEvent: <T extends TEvent>(data: ExtraData<T>) => {
        const { pageCtxId } = getContext();
        bianka.reportActivity({
            type: BIANKA_ACTIVITY_TYPES.CLICK,
            event: data.event,
            page: {
                name: data.pageName,
                ctxId: pageCtxId,
            },
            additionalData: data.additionalData,
        });
    },

    report_Viewed_tier_level_updated: ({
        pageName,
        componentName = 'tier_level_updated',
        componentType = ComponentType.MODAL,
        tierLevel,
    }: BqEventPayload) => {
        eventsApi.reportImpressionEvent<ViewedTierLevelUpdated>({
            pageName,
            event: {
                type: 'viewed_tier_level_updated',
            },
            additionalData: {
                type: ViewedTierLevelUpdated,
                message: {
                    user: {
                        id: getContext().userId as number,
                    },
                    organization: {
                        id: getContext().organizationId as string,
                    },
                    component: {
                        name: componentName,
                        type: componentType,
                    },
                    eventSource: EventSource.TIER_LEVEL_UPDATED_MODAL,
                    tierLevel: getTierLevel(tierLevel),
                },
            },
        });
    },

    report_Viewed_loyalty_credits_given: ({ pageName, tierLevel }: BqEventPayload) => {
        eventsApi.reportImpressionEvent<ViewedLoyaltyCreditsGiven>({
            pageName,
            event: {
                type: 'viewed_loyalty_credits_given',
            },
            additionalData: {
                type: ViewedLoyaltyCreditsGiven,
                message: {
                    user: {
                        id: getContext().userId as number,
                    },
                    organization: {
                        id: getContext().organizationId as string,
                    },
                    component: {
                        name: 'loyalty_credits_given',
                        type: ComponentType.MODAL,
                    },
                    tierLevel: getTierLevel(tierLevel),
                },
            },
        });
    },
    report_Viewed_loyalty_credits_on_the_way: ({ pageName, tierLevel }: BqEventPayload) => {
        eventsApi.reportImpressionEvent<ViewedLoyaltyCreditsOnTheWay>({
            pageName,
            event: {
                type: 'viewed_loyalty_credits_on_the_way',
            },
            additionalData: {
                type: ViewedLoyaltyCreditsOnTheWay,
                message: {
                    user: {
                        id: getContext().userId as number,
                    },
                    component: {
                        name: 'loyalty_credits_on_the_way',
                        type: ComponentType.MODAL,
                    },
                    tierLevel: getTierLevel(tierLevel),
                },
            },
        });
    },
    viewed_loyalty_credit_indication_badge: ({
        pageName,
        componentName,
        tierLevel,
    }: LoyaltyCreditIndicationEventPayload) => {
        eventsApi.reportImpressionEvent<ViewedLoyaltyCreditIndicationBadge>({
            pageName,
            event: {
                type: 'viewed_loyalty_credit_indication_badge',
            },
            additionalData: {
                type: ViewedLoyaltyCreditIndicationBadge,
                message: {
                    user: {
                        id: getContext().userId as number,
                    },
                    organization: {
                        id: getContext().organizationId as string,
                    },
                    component: {
                        name: componentName,
                        type: ComponentType.UNSPECIFIED,
                    },
                    tierLevel: getTierLevel(tierLevel),
                },
            },
        });
    },
};
